/**
 * News — the articles behind /news, /news/:slug, and the landing page's
 * "What's new" strip.
 *
 * Not built from `cms-tabs.ts`'s factory, unlike FAQ and the rest, for three
 * reasons that all change the shape of the screen: it paginates, it uploads a
 * header image, and the slug is generated server-side from the title and is
 * therefore read-only here.
 *
 * ─── The image is uploaded before the article is saved ──────────────────────
 * `POST /admin/news/upload-image` is a separate endpoint that returns a URL;
 * the article then carries that URL in `imageUrl`. So choosing a file uploads
 * it immediately and writes the returned URL into the form's own field, which
 * is still just text and can also be pasted. A file left unsaved in a
 * cancelled form is an orphan in the uploads directory — the endpoint offers
 * nothing to clean that up, and inventing a cleanup this panel cannot verify
 * would be worse than the orphan.
 */
import { createNews, deleteNews, listNews, updateNews, uploadNewsImage } from '../admin-api';
import type { NewsRow, Paged } from '../admin-api.types';
import {
  badge, checkbox, errorText, esc, field, fmtDate, pager, readForm, section,
} from '../admin-kit';
import type { AdminTab } from '../admin-kit';

export const newsTab: AdminTab = {
  id: 'news',
  label: 'News',
  group: 'CMS',

  mount(host, ctx) {
    let live = true;
    let pageNo = 1;
    let data: Paged<NewsRow> | null = null;
    let error = '';
    let editing: string | null = null;

    function form(row: NewsRow | null): string {
      return `
        <form class="tg-adm-form" data-form="${row ? esc(row.id) : 'new'}">
          ${row ? `<p class="tg-adm-hint">Slug: <span class="tg-adm-mono">${esc(row.slug)}</span>
            — generated from the title when the article was created, and never changed after.</p>` : ''}
          ${field('title', 'Title', row?.title ?? '')}
          ${field('summary', 'Summary', row?.summary ?? '', { hint: 'One or two lines, shown in the index' })}
          ${field('content', 'Body', row?.content ?? '', { long: true, hint: 'Markdown' })}
          <div class="tg-adm-form-row">
            ${field('tag', 'Tag', row?.tag ?? '')}
            ${field('imageUrl', 'Header image URL', row?.imageUrl ?? '', {
    hint: 'Uploaded below, or pasted',
  })}
            <label class="tg-adm-btn">Upload image
              <input type="file" accept="image/*" data-news-image hidden>
            </label>
          </div>
          ${checkbox('published', 'Published', row ? row.published : false)}
          <div class="tg-adm-actions">
            <button type="submit" class="tg-adm-btn tg-adm-btn-primary">${row ? 'Save' : 'Create'}</button>
            <button type="button" class="tg-adm-btn" data-act="cancel">Cancel</button>
            ${row ? `<button type="button" class="tg-adm-btn tg-adm-btn-danger"
              data-act="delete" data-id="${esc(row.id)}">Delete</button>` : ''}
          </div>
        </form>`;
    }

    function body(): string {
      if (error) return `<p class="tg-adm-empty">${esc(error)}</p>`;
      if (!data) return '<p class="tg-adm-empty">Loading…</p>';

      const creating = editing === 'new' ? form(null) : '';
      const list = data.items.map((row) => {
        if (editing === row.id) return form(row);
        return `
          <div class="tg-adm-cmsrow">
            <div class="tg-adm-cmsrow-main">
              ${row.published ? badge('published', 'ok') : badge('draft', 'mute')}
              <span><strong>${esc(row.title)}</strong> — ${esc(row.summary)}</span>
            </div>
            <div class="tg-adm-rowacts">
              <span class="tg-adm-hint">${esc(fmtDate(row.publishedAt ?? row.createdAt))}</span>
              <button type="button" class="tg-adm-btn" data-act="edit" data-id="${esc(row.id)}">Edit</button>
            </div>
          </div>`;
      }).join('');

      if (!list && !creating) return '<p class="tg-adm-empty">No articles yet.</p>';
      return `${creating}${list}${pager(data.page, data.pageSize, data.total)}`;
    }

    const paint = () => {
      host.innerHTML = section(
        'News',
        'The public news feed. Unpublished articles are invisible to players.',
        body(),
        '<button type="button" class="tg-adm-btn tg-adm-btn-primary" data-act="new">Write one</button>',
      );
    };

    async function load(): Promise<void> {
      error = '';
      try {
        const result = await listNews(pageNo);
        if (!live) return;
        data = result;
      } catch (err) {
        if (!live) return;
        error = errorText(err);
      }
      paint();
    }

    async function act(what: string, run: () => Promise<unknown>): Promise<void> {
      try {
        await run();
        if (!live) return;
        editing = null;
        ctx.notify(what);
      } catch (err) {
        if (!live) return;
        ctx.notify(errorText(err), 'err');
        return;
      }
      await load();
    }

    const onClick = (e: MouseEvent) => {
      const el = (e.target as HTMLElement).closest<HTMLElement>('[data-act],[data-page]');
      if (!el) return;
      if (el.dataset.page) { pageNo = Number(el.dataset.page); void load(); return; }
      switch (el.dataset.act) {
        case 'new': editing = 'new'; paint(); break;
        case 'edit': editing = el.dataset.id!; paint(); break;
        case 'cancel': editing = null; paint(); break;
        case 'delete': {
          const id = el.dataset.id!;
          ctx.confirm({
            title: 'Delete this article?',
            body: 'Its URL stops resolving. Unpublishing instead keeps the article and hides it.',
            confirmLabel: 'Delete',
            onConfirm: () => void act('Article deleted.', () => deleteNews(id)),
          });
          break;
        }
        default: break;
      }
    };

    const onChange = (e: Event) => {
      const input = e.target as HTMLInputElement;
      if (!input.hasAttribute('data-news-image')) return;
      const file = input.files?.[0];
      if (!file) return;
      const urlField = input.closest('form')?.querySelector<HTMLInputElement>('[name="imageUrl"]');
      void (async () => {
        try {
          const { url } = await uploadNewsImage(file);
          if (!live) return;
          // Written into the field rather than saved: the article may not exist
          // yet, and the URL is only meaningful once it is submitted with it.
          if (urlField) urlField.value = url;
          ctx.notify('Image uploaded. Save the article to attach it.');
        } catch (err) {
          if (live) ctx.notify(errorText(err), 'err');
        }
      })();
    };

    const onSubmit = (e: SubmitEvent) => {
      const formEl = e.target as HTMLFormElement;
      const target = formEl.dataset.form;
      if (!target) return;
      e.preventDefault();
      const v = readForm(formEl);
      const payload: Partial<NewsRow> = {
        title: String(v.title || ''),
        summary: String(v.summary || ''),
        content: String(v.content || ''),
        tag: String(v.tag || '') || undefined,
        published: Boolean(v.published),
        // Empty means "no image" and is sent as null, which the DTO accepts —
        // an empty string would be stored and rendered as a broken <img src="">.
        imageUrl: String(v.imageUrl || '') || null,
      };
      if (!payload.title || !payload.summary || !payload.content) {
        ctx.notify('Title, summary and body are all required.', 'err');
        return;
      }
      if (target === 'new') {
        void act('Article created.', () => createNews(payload));
      } else {
        void act('Article saved.', () => updateNews(target, payload));
      }
    };

    host.addEventListener('click', onClick);
    host.addEventListener('change', onChange);
    host.addEventListener('submit', onSubmit as EventListener);

    paint();
    void load();

    return () => {
      live = false;
      host.removeEventListener('click', onClick);
      host.removeEventListener('change', onChange);
      host.removeEventListener('submit', onSubmit as EventListener);
    };
  },
};
